// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Comparing two versions (design doc §9.5, US-C-05).
 *
 * The case worth designing for is the *empty* diff. After a like-for-like swap the two
 * snapshots are identical, and a comparison that renders three empty tables reads as
 * "nothing happened" — the opposite of the truth. So when the server reports `identical`,
 * the page says so in a sentence and shows the maintenance events that fall between the
 * two versions, which is where the change actually is.
 *
 * With modules (component design §9.4) the comparison goes down a layer at a time: a slot
 * that holds another module shows both in full, BOM version included; one that holds the
 * same module at a newer version opens onto what changed inside it, and below that. A swap
 * to a module built to another BOM version (§7 rule 5) is marked on its row and named in
 * the line above the tables.
 *
 * A position that now holds a different material — a substitute fitted (§8.6.1) — is the
 * change a reader most needs and the easiest to miss: its serial may be the same on both
 * sides. So every side shows its material, a changed one is marked 「新物料」, and a line
 * above the tables names every such position, inside modules too.
 */

import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api/endpoints";
import type { DiffEntry, ModuleChange, PinnedModule, VersionCompare } from "@/api/types";
import { materialChangedBetween, materialOf, summarizePart } from "@/archive/partState";
import { ChangeSummary } from "@/components/ChangeType";
import { ErrorBanner } from "@/components/ErrorBanner";
import { EventDetail, PartCell } from "@/components/MaintenanceEventDetail";
import { useArchive } from "./ArchiveDetailPage";

export function ArchiveCompare() {
  const { t } = useTranslation();
  const { archiveId = "" } = useParams();
  const { archive } = useArchive();

  const [from, setFrom] = useState(Math.max(1, archive.current_version - 1));
  const [to, setTo] = useState(archive.current_version);
  // Which events are opened up; several at once, to read side by side.
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (eventId: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });

  const compare = useQuery({
    queryKey: ["archive-compare", archiveId, from, to],
    queryFn: () => api.archives.compare(archiveId, from, to),
    enabled: from > 0 && to > 0,
  });

  const versionOptions = Array.from({ length: archive.current_version }, (_, i) => i + 1);

  return (
    <>
      <div className="filters">
        <select
          aria-label={t("compare.fromVersion")}
          value={from}
          onChange={(event) => setFrom(Number(event.target.value))}
        >
          {versionOptions.map((version) => (
            <option key={version} value={version}>
              v{version}
            </option>
          ))}
        </select>
        <span>→</span>
        <select
          aria-label={t("compare.toVersion")}
          value={to}
          onChange={(event) => setTo(Number(event.target.value))}
        >
          {versionOptions.map((version) => (
            <option key={version} value={version}>
              v{version}
            </option>
          ))}
        </select>
      </div>

      <ErrorBanner error={compare.error} />

      {compare.data?.diff.identical && (
        <div className="banner banner-info">{t("maintenance.identicalHint")}</div>
      )}

      {compare.data && <NewMaterialSummary compare={compare.data} />}

      {compare.data && (
        <>
          <DiffTable rows={compare.data.diff.added} kind="added" />
          <DiffTable rows={compare.data.diff.removed} kind="removed" />
          <DiffTable rows={compare.data.diff.changed} kind="changed" />
          {(compare.data.modules ?? []).length > 0 && (
            <>
              <h2>{t("compare.modules")}</h2>
              <ModuleChanges changes={compare.data.modules ?? []} />
            </>
          )}

          <h2>{t("maintenance.title")}</h2>
          {/* Each opens in place to what it did, as on the 更换记录 tab: the diff above says
              what differs between the two versions, these say which change did it. */}
          <table className="event-table">
            <thead>
              <tr>
                <th>{t("maintenance.sequence")}</th>
                <th>{t("maintenance.occurredAt")}</th>
                <th>{t("maintenance.changeColumn")}</th>
              </tr>
            </thead>
            <tbody>
              {compare.data.events.map((event) => {
                const expanded = open.has(event.event_id);
                // Recorded on a fitted module, as on the 更换记录 tab.
                const owner = event.archive_id ?? archiveId;
                return (
                  <Fragment key={event.event_id}>
                    <tr
                      className={expanded ? "event-row is-open" : "event-row"}
                      onClick={() => toggle(event.event_id)}
                    >
                      <td>
                        <button
                          type="button"
                          className="row-activate"
                          aria-expanded={expanded}
                          aria-controls={`compare-event-${event.event_id}`}
                        >
                          <span className="disclosure" aria-hidden="true">
                            {expanded ? "▾" : "▸"}
                          </span>
                          #{event.sequence_no}
                        </button>
                        {event.archive_id && (
                          <span
                            className="event-module"
                            onClick={(click) => click.stopPropagation()}
                          >
                            {event.module_type && (
                              <span className="module-badge">{event.module_type}</span>
                            )}{" "}
                            <Link to={`/archives/${owner}/maintenance`}>{event.archive_no}</Link>
                            {event.source_sequence_no != null && ` #${event.source_sequence_no}`}
                          </span>
                        )}
                      </td>
                      <td>{new Date(event.occurred_at).toLocaleString()}</td>
                      <td>
                        <ChangeSummary changeType={event.change_type} reason={event.reason} />
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="event-detail-row" id={`compare-event-${event.event_id}`}>
                        <td colSpan={3}>
                          <EventDetail archiveId={owner} event={event} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function DiffTable({ rows, kind }: { rows: DiffEntry[]; kind: "added" | "removed" | "changed" }) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <>
      <h2>{t(`compare.${kind}`)}</h2>
      <table>
        <thead>
          <tr>
            <th>{t("archive.position")}</th>
            <th>{t("compare.before")}</th>
            <th>{t("compare.after")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const before = summarizePart(row.before);
            const after = summarizePart(row.after);
            const materialBefore = materialOf(row.before);
            const materialAfter = materialOf(row.after);
            const newMaterial = materialChangedBetween(row.before, row.after);
            return (
              <tr key={`${kind}-${row.position_code}`} className={`diff-${kind}`}>
                <td>
                  {row.position_name}
                  {newMaterial && (
                    <span className="badge badge-new-material">{t("compare.newMaterial")}</span>
                  )}
                </td>
                <td>
                  <PartCell value={before} material={materialBefore} />
                </td>
                <td>
                  <PartCell
                    value={after}
                    material={materialAfter}
                    valueChanged={kind === "changed" && after !== before}
                    materialChanged={newMaterial}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/** One line naming every position that now holds a different material, down through the
 *  modules — so it is seen before the tables are read. Nothing when there is none. */
function NewMaterialSummary({ compare }: { compare: VersionCompare }) {
  const { t } = useTranslation();
  const positions = [
    ...newMaterialPositions(compare.diff.changed, ""),
    ...(compare.modules ?? []).flatMap((change) => newMaterialInModule(change, "")),
  ];
  const crossBom = (compare.modules ?? []).flatMap((change) => crossBomSlots(change, ""));
  if (positions.length === 0 && crossBom.length === 0) return null;
  return (
    <div className="banner banner-warn">
      {crossBom.length > 0 && (
        <div>
          {t("compare.moduleBomSummary", {
            count: crossBom.length,
            positions: crossBom.join("、"),
          })}
        </div>
      )}
      {positions.length > 0 && (
        <div>
          {t("compare.newMaterialSummary", {
            count: positions.length,
            positions: positions.join("、"),
          })}
        </div>
      )}
    </div>
  );
}

/** Every module slot, down the layers, now holding a module built to another BOM:
 *  「机械臂#1（BOM 1.0 → 1.1）」. */
function crossBomSlots(change: ModuleChange, prefix: string): string[] {
  const before = change.before?.bom_version ?? "—";
  const after = change.after?.bom_version ?? "—";
  const here = bomVersionChanged(change)
    ? [`${prefix}${change.position_name}（BOM ${before} → ${after}）`]
    : [];
  const below = `${prefix}${change.position_name} / `;
  return [...here, ...change.modules.flatMap((inner) => crossBomSlots(inner, below))];
}

function newMaterialPositions(rows: DiffEntry[], prefix: string): string[] {
  return rows
    .filter((row) => materialChangedBetween(row.before, row.after))
    .map((row) => prefix + row.position_name);
}

function newMaterialInModule(change: ModuleChange, prefix: string): string[] {
  const here = `${prefix}${change.position_name} / `;
  return [
    ...newMaterialPositions(change.diff?.changed ?? [], here),
    ...change.modules.flatMap((inner) => newMaterialInModule(inner, here)),
  ];
}

/** Module slots that changed, each side in full — type, archive number, SN, the version
 *  pinned and the BOM it was built to — with what changed inside a module that stayed,
 *  layer by layer. A swap across BOM versions is marked, and so is the BOM that differs:
 *  it is the one thing on the row that does not read as an ordinary swap. */
function ModuleChanges({ changes }: { changes: ModuleChange[] }) {
  const { t } = useTranslation();
  return (
    <table className="module-changes">
      <thead>
        <tr>
          <th>{t("compare.moduleSlot")}</th>
          <th>{t("compare.before")}</th>
          <th>{t("compare.after")}</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((change) => {
          const sameModule =
            !!change.before &&
            !!change.after &&
            change.before.archive_id === change.after.archive_id;
          const bomChanged = bomVersionChanged(change);
          const inner =
            !!change.diff &&
            change.diff.added.length + change.diff.removed.length + change.diff.changed.length > 0;
          return (
            <Fragment key={change.position_code}>
              <tr className="diff-changed">
                <td>
                  {change.position_name}
                  <span className="badge">
                    {t(sameModule ? "compare.moduleUpdated" : "compare.moduleSwapped")}
                  </span>
                  {bomChanged && (
                    <span className="badge badge-new-material">
                      {t("compare.moduleBomChanged")}
                    </span>
                  )}
                </td>
                <td>
                  <PinnedCell module={change.before} />
                </td>
                <td>
                  <PinnedCell module={change.after} other={change.before} bomChanged={bomChanged} />
                </td>
              </tr>
              {(inner || change.modules.length > 0) && (
                <tr className="module-change-inner">
                  <td colSpan={3}>
                    {change.diff && (
                      <>
                        <DiffTable rows={change.diff.added} kind="added" />
                        <DiffTable rows={change.diff.removed} kind="removed" />
                        <DiffTable rows={change.diff.changed} kind="changed" />
                      </>
                    )}
                    {change.modules.length > 0 && <ModuleChanges changes={change.modules} />}
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

/** Both sides hold a module, and they were built to different BOMs. */
function bomVersionChanged(change: ModuleChange): boolean {
  return (
    !!change.before &&
    !!change.after &&
    (change.before.source_bom_id ?? null) !== (change.after.source_bom_id ?? null)
  );
}

/** One side of a module slot: what it held, in full; what differs from `other` in bold. */
function PinnedCell({
  module,
  other,
  bomChanged = false,
}: {
  module: PinnedModule | null;
  other?: PinnedModule | null;
  bomChanged?: boolean;
}) {
  const { t } = useTranslation();
  if (!module) return <span className="muted">{t("compare.emptySlot")}</span>;
  const swapped = !!other && other.archive_id !== module.archive_id;
  return (
    <div className="pinned-module">
      <span className={swapped ? "is-changed" : undefined}>
        {module.module_type && <span className="module-badge">{module.module_type}</span>}{" "}
        <Link to={`/archives/${module.archive_id}`}>{module.archive_no ?? module.archive_id}</Link>
      </span>
      {module.module_sn && (
        <span className={swapped ? "serial is-changed" : "serial"}>{module.module_sn}</span>
      )}
      <span className="pinned-module-versions">
        {t("compare.pinnedVersion", { version: module.version_no })}
        {" · "}
        <span className={bomChanged ? "is-changed module-bom-changed" : undefined}>
          {t("compare.bomVersion", { version: module.bom_version ?? t("compare.noBomVersion") })}
        </span>
      </span>
    </div>
  );
}
